<template>
  <div class="user-payment-history">
    <div class="history-header">
      <h3>История моих платежей</h3>
      <a-button 
        type="primary" 
        @click="refreshHistory"
        :loading="loading"
      >
        <ReloadOutlined />
        Обновить
      </a-button>
    </div>

    <div v-if="loading" class="loading-container">
      <a-spin size="large" />
      <p>Загрузка истории платежей...</p>
    </div>

    <div v-else-if="payments.length === 0" class="no-payments">
      <div class="no-payments-message">
        <DollarOutlined class="no-payments-icon" />
        <h4>Нет платежей</h4>
        <p>У вас пока нет зарегистрированных платежей в этой команде</p>
      </div>
    </div>

    <div v-else class="payments-list">
      <a-list
        :data-source="payments"
        :pagination="pagination"
        item-layout="horizontal"
      >
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #avatar>
                <a-avatar :style="{ backgroundColor: getStatusColor(item.status) }">
                  <DollarOutlined />
                </a-avatar>
              </template>
              <template #title>
                <div class="payment-title">
                  <span class="payment-name">{{ item.payment_type_name }}</span>
                  <a-tag :color="getStatusColor(item.status)">
                    {{ getStatusText(item.status) }}
                  </a-tag>
                </div>
              </template>
              <template #description>
                <div class="payment-details">
                  <div class="payment-amount">
                    <strong>{{ formatAmount(item.amount) }} ₽</strong>
                  </div>
                  <div class="payment-date">
                    Дата: {{ formatDate(item.payment_date) }}
                  </div>
                  <div v-if="item.comment" class="payment-comment">
                    Комментарий: {{ item.comment }}
                  </div>
                  <div v-if="item.receipt_url" class="payment-receipt">
                    <a-button 
                      type="link" 
                      size="small"
                      @click="viewReceipt(item.receipt_url)"
                    >
                      <EyeOutlined />
                      Просмотреть квитанцию
                    </a-button>
                  </div>
                </div>
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </div>
  </div>
</template>

<script>
import { api } from '../services/api';
import { List, ListItem, ListItemMeta, Avatar, Button, Tag, Spin } from 'ant-design-vue';
import { 
  DollarOutlined, 
  ReloadOutlined, 
  EyeOutlined 
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';

export default {
  name: 'UserPaymentHistory',
  components: {
    'a-list': List,
    'a-list-item': ListItem,
    'a-list-item-meta': ListItemMeta,
    'a-avatar': Avatar,
    'a-button': Button,
    'a-tag': Tag,
    'a-spin': Spin,
    DollarOutlined,
    ReloadOutlined,
    EyeOutlined
  },
  props: {
    teamId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      payments: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} платежей`
      }
    };
  },
  async mounted() {
    await this.loadPaymentHistory();
  },
  watch: {
    teamId(newTeamId, oldTeamId) {
      if (newTeamId !== oldTeamId) {
        this.loadPaymentHistory();
      }
    }
  },
  methods: {
    async loadPaymentHistory() {
      try {
        this.loading = true;
        const response = await api.getUserPaymentHistory(this.teamId);
        this.payments = response || [];
        this.pagination.total = this.payments.length;
      } catch (error) {
        console.error('Error loading payment history:', error);
        this.$message.error('Ошибка при загрузке истории платежей');
        this.payments = [];
      } finally {
        this.loading = false;
      }
    },

    async refreshHistory() {
      await this.loadPaymentHistory();
    },

    getStatusColor(status) {
      const colors = {
        'completed': '#52c41a',
        'pending': '#faad14',
        'rejected': '#ff4d4f',
        'cancelled': '#d9d9d9'
      };
      return colors[status] || '#1890ff';
    },

    getStatusText(status) {
      const texts = {
        'completed': 'Оплачен',
        'pending': 'На проверке',
        'rejected': 'Отклонён',
        'cancelled': 'Отменён'
      };
      return texts[status] || status;
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return amount.toLocaleString('ru-RU');
    },

    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD.MM.YYYY');
    },

    viewReceipt(url) {
      if (!url) return;
      
      // Открываем квитанцию в новом окне или показываем модал
      if (window.Telegram && window.Telegram.WebApp) {
        // В Telegram Mini App показываем сообщение
        this.$message.info('Откройте квитанцию в браузере');
        window.open(url, '_blank');
      } else {
        // В обычном браузере открываем в новом окне
        window.open(url, '_blank');
      }
    }
  }
};
</script>

<style scoped>
.user-payment-history {
  padding: 16px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.history-header h3 {
  margin: 0;
  color: #1890ff;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.no-payments {
  text-align: center;
  padding: 40px;
}

.no-payments-message {
  color: #666;
}

.no-payments-icon {
  font-size: 48px;
  color: #d9d9d9;
  margin-bottom: 16px;
}

.payments-list {
  margin-top: 16px;
}

.payment-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-name {
  font-weight: 500;
}

.payment-details {
  margin-top: 8px;
}

.payment-amount {
  font-size: 16px;
  margin-bottom: 4px;
}

.payment-date {
  color: #666;
  font-size: 14px;
  margin-bottom: 4px;
}

.payment-comment {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
  font-style: italic;
}

.payment-receipt {
  margin-top: 8px;
}
</style>
